/* eslint-disable react-refresh/only-export-components -- Exports provider, hooks, and utilities */
/**
 * I18nProvider — React context wrapper around the translation table.
 *
 * Mirrors the prototype's `window.__i18n` global by exposing:
 *   - `lang`: the current language code
 *   - `setLang(lang)`: change language; persists to localStorage as `tru-lang`
 *   - `t(key)`: lookup with fallback chain
 *       current-lang dict → English dict → raw key
 *
 * Persistence — the chosen language survives reloads via localStorage,
 * matching the prototype behavior. SSR-safe guards check `typeof window`
 * so the provider can render on the server without errors.
 *
 * Document side-effects — sets `<html lang="...">` and `dir="rtl"` for
 * RTL locales (currently `ar`). This matches the prototype which writes
 * `documentElement.lang` and `documentElement.dir` on every language
 * change.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { TRANSLATIONS, RTL_LANGS, SUPPORTED_LANGS } from './translations';
import type { SupportedLang } from './translations';
import api from '../services/api';

const STORAGE_KEY = 'tru-lang';
const DEFAULT_LANG: SupportedLang = 'en';

const LOCALE_BY_LANG: Record<SupportedLang, string> = {
  en: 'en-US',
  es: 'es-ES',
  fr: 'fr-FR',
  de: 'de-DE',
  pt: 'pt-BR',
  ja: 'ja-JP',
  zh: 'zh-CN',
  ko: 'ko-KR',
  it: 'it-IT',
  nl: 'nl-NL',
  ar: 'ar-SA',
  hi: 'hi-IN',
  th: 'th-TH',
  pl: 'pl-PL',
  tr: 'tr-TR',
};

const TRANSLATOR_URL = `${(import.meta.env.VITE_apiBaseUrl as string)?.trim()}/ApplicationTranslator`;

type TranslatorPayload = Record<string, unknown>;

type TranslatorEnvelope = {
  Data?: unknown;
  data?: unknown;
};

const BACKEND_KEY_ALIASES: Record<string, string> = {
  // Existing shell keys -> backend translator payload paths
  'header.appname': 'header.headerAppText',
  'header.brandPrimary': 'header.headerPgmText',
  'header.brandSecondary': 'header.headerAppText',
  'tab.newChat': 'chat.newchat',
  'tab.chat': 'workspacemenu.1.name',
  'dock.agents': 'workspacemenu.2.name',
  'dock.pulse': 'workspacemenu.10.name',
  'chat.askAnything': 'chat.askAI',
  'chat.askFollowUp': 'chat.placeholderText',
  'chat.placeholder': 'chat.placeholderText',
  'chat.send': 'chat.send',
  // Settings/profile/header keys used by MainHeader
  'settings.title': 'settings.headerTitle',
  'settings.memory': 'header.memorysettings',
  'settings.referenceSavedMemories': 'header.referencesavedmemories',
  'settings.referenceChatHistory': 'header.referencechathistory',
  'settings.manageSavedMemories': 'header.managesavedmemories',
  'header.language': 'header.headerLanguage',
  'header.darkMode': 'header.darkMode',
  'header.lightMode': 'header.lightMode',
  'header.welcomeTutorial': 'header.welcomeTutorial',
  'header.systemCard': 'header.systemCard',
  'header.guides': 'workspacemenu.8.name',
  'profile.disclaimer': 'disclaimer.disclaimerlink',
  'profile.title': 'header.headerAppText',
  'common.close': 'action.close',
  'common.cancel': 'action.cancel',
  'common.submit': 'action.submit',
  // Feedback modal
  'feedback.header': 'applicationFeedBack.header',
  'feedback.description.1': 'applicationFeedBack.descriptionLine1',
  'feedback.description.2.1': 'applicationFeedBack.descriptionLine2_1',
  'feedback.description.2.2': 'applicationFeedBack.descriptionLine2_2',
  'feedback.description.2.3': 'applicationFeedBack.descriptionLine2_3',
  'feedback.categoryLabel': 'applicationFeedBack.categoryLabel',
  'feedback.customFeedbackPlaceholder': 'applicationFeedBack.customFeedbackPlaceholder',
  'feedback.ratingLabel': 'applicationFeedBack.ratingLabel',
  'feedback.footnote0': 'applicationFeedBack.footnoteLabel0',
  'feedback.footnote1': 'applicationFeedBack.footnoteLabel1',
  'feedback.footnote2': 'applicationFeedBack.footnoteLabel2',
  'feedback.option.general': 'applicationFeedBack.category.0',
  'feedback.option.accuracy': 'applicationFeedBack.category.1',
  'feedback.option.speed': 'applicationFeedBack.category.2',
  'feedback.option.usability': 'applicationFeedBack.category.3',
  'feedback.option.feature': 'applicationFeedBack.category.4',
  'feedback.option.bug': 'applicationFeedBack.category.5',
  'feedback.categoryError': 'applicationFeedBack.categoryError',
  'feedback.ratingValidationMessage': 'applicationFeedBack.ratingValidationMessage',
};

function normalizeTranslatorPayload(value: unknown): TranslatorPayload {
  // The backend Ok(JsonConvert.SerializeObject(...)) double-encodes: the HTTP body is a JSON string literal.
  // Parse it once more to recover the actual object.
  if (typeof value === 'string') {
    try { value = JSON.parse(value); } catch { return {}; }
  }
  if (!value || typeof value !== 'object') {
    return {};
  }

  const envelope = value as TranslatorEnvelope;
  const nested = envelope.Data ?? envelope.data;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    return nested as TranslatorPayload;
  }

  return value as TranslatorPayload;
}

function readSegmentValue(cursor: unknown, segment: string): unknown {
  if (!cursor || typeof cursor !== 'object') {
    return null;
  }

  if (Array.isArray(cursor)) {
    const index = Number.parseInt(segment, 10);
    if (!Number.isNaN(index) && index >= 0 && index < cursor.length) {
      return cursor[index];
    }
    return null;
  }

  const objectCursor = cursor as Record<string, unknown>;
  if (segment in objectCursor) {
    return objectCursor[segment];
  }

  const lower = segment.toLowerCase();
  const matchedKey = Object.keys(objectCursor).find((key) => key.toLowerCase() === lower);
  if (!matchedKey) {
    return null;
  }
  return objectCursor[matchedKey];
}

function getValueAtPath(value: unknown, path: string): unknown {
  const segments = path.split('.').filter(Boolean);
  let cursor: unknown = value;
  for (const segment of segments) {
    cursor = readSegmentValue(cursor, segment);
    if (cursor === null || typeof cursor === 'undefined') {
      return null;
    }
  }
  return cursor;
}

function readPersistedLang(): SupportedLang {
  if (typeof window === 'undefined') return DEFAULT_LANG;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v && (SUPPORTED_LANGS as readonly string[]).includes(v)) {
      return v as SupportedLang;
    }
  } catch {
    // localStorage may throw in private-browsing modes — fall through.
  }
  return DEFAULT_LANG;
}

function persistLang(lang: SupportedLang): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // swallow — persistence is best-effort, not a correctness invariant
  }
}

function applyDocumentLang(lang: SupportedLang): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = lang;
  document.documentElement.dir = RTL_LANGS.has(lang) ? 'rtl' : 'ltr';
}

export interface I18nContextValue {
  lang: SupportedLang;
  setLang: (lang: SupportedLang) => void;
  t: (key: string) => string;
  getValue: (path: string) => unknown;
}

const I18nContext = createContext<I18nContextValue | undefined>(undefined);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<SupportedLang>(() => readPersistedLang());
  const [remoteByLang, setRemoteByLang] = useState<Partial<Record<SupportedLang, TranslatorPayload>>>({});

  // Apply lang/dir attributes whenever the language changes.
  useEffect(() => {
    applyDocumentLang(lang);
  }, [lang]);

  const setLang = useCallback((next: SupportedLang) => {
    persistLang(next);
    setLangState(next);
  }, []);

  const getValue = useCallback(
    (path: string): unknown => {
      const remote = remoteByLang[lang];
      if (!remote) return undefined;
      const aliasPath = BACKEND_KEY_ALIASES[path] ?? path;
      return getValueAtPath(remote, aliasPath);
    },
    [lang, remoteByLang]
  );

  // Fetch backend translator payload per language and cache in-memory.
  useEffect(() => {
    if (remoteByLang[lang]) {
      return;
    }

    const locale = LOCALE_BY_LANG[lang] ?? LOCALE_BY_LANG.en;
    const payload = [
      { key: 'Language', value: locale },
      { key: 'ctry', value: locale.split('-')[1] ?? 'US' },
      { key: 'SCREEN.TYPE', value: 'Standalone' },
    ];

    let cancelled = false;
    api.fetch<unknown>(`${TRANSLATOR_URL}/${encodeURIComponent(locale)}`, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
      .then((data) => {
        if (cancelled) return;
        setRemoteByLang((prev) => ({
          ...prev,
          [lang]: normalizeTranslatorPayload(data),
        }));
      })
      .catch((error) => {
        console.warn('[I18nProvider] Failed to fetch backend translator payload', {
          lang,
          locale,
          error,
        });
      });

    return () => {
      cancelled = true;
    };
  }, [lang, remoteByLang]);

  // `t` lookup with the three-tier fallback chain. Memoized on `lang`
  // because the dictionary reference is stable and the function is
  // consumed by many components via context.
  const t = useCallback(
    (key: string): string => {
      const backendValue = getValue(key);
      if (typeof backendValue === 'string' && backendValue.length > 0) {
        return backendValue;
      }

      const dict = TRANSLATIONS[lang];
      if (dict && key in dict) return dict[key];
      const enDict = TRANSLATIONS.en;
      if (key in enDict) return enDict[key];
      return key;
    },
    [getValue, lang]
  );

  const value = useMemo<I18nContextValue>(
    () => ({ lang, setLang, t, getValue }),
    [getValue, lang, setLang, t]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/**
 * Subscribe to the i18n context. Throws if used outside the provider.
 */
export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error('useI18n must be called inside <I18nProvider>');
  }
  return ctx;
}

/**
 * Convenience hook — returns just `t`. Use this when a component only
 * needs to translate (the common case).
 */
export function useT(): (key: string) => string {
  return useI18n().t;
}
